import { redirectToUrl, getRedirectUrl } from 'CmsManagePreferences/utils/url';
import { PAGE_TYPE } from 'CmsManagePreferences/utils/constants';
import { getConfirmationPageUrl } from 'CmsManagePreferences/utils/url';

/**
 * Represents an HTTP client for making API requests.
 */
export default class HttpClient {
  constructor() {
    this.httpClient = new XMLHttpRequest();
  }
  redirectToFallbackPage() {
    const fallbackUrl = getRedirectUrl(PAGE_TYPE.failure);
    redirectToUrl(fallbackUrl);
  }
  serializePayload(payload) {
    const params = new URLSearchParams();
    payload.forEach((value, key) => {
      params.append(key, value);
    });
    return params.toString();
  }

  /**
   * Performs a GET request to the specified URL and returns a Promise that resolves to the response data.
   * If the response indicates a redirect, the client will be redirected to the specified URL.
   * If an error occurs during the request, the client will be redirected to a fallback page.
   *
   * @param url - The URL to send the GET request to.
   * @returns A Promise that resolves to the response data.
   * @throws If an error occurs during the request. Errors are not re-thrown but handled by redirecting to a fallback page.
   */
  async get(url) {
    return new Promise((resolve, reject) => {
      const client = this.httpClient;
      client.open('GET', url);
      client.setRequestHeader('Cache-Control', 'no-cache');
      client.onreadystatechange = () => {
        if (client.readyState === XMLHttpRequest.DONE || client.readyState === 4) {
          if (client.status === 200) {
            try {
              const response = JSON.parse(client.responseText);
              // 302s are now handled as 200s due to Proxy constraints
              if (response && response.shouldRedirect && response.url) {
                redirectToUrl(response.url);
              }
              resolve(response);
            } catch (error) {
              this.redirectToFallbackPage();
            }
          } else if (client.status >= 400 && client.status <= 500) {
            this.redirectToFallbackPage();
          }
        }
      };
      client.onerror = () => {
        this.redirectToFallbackPage();
      };
      client.send();
    });
  }

  /**
   * Performs a POST request to the specified URL with the given payload and returns a Promise that resolves to the response data.
   * If the response indicates a redirect, the client will be redirected to the specified URL.
   * If an error occurs during the request, the client will be redirected to a fallback page.
   *
   * @param url - The URL to send the POST request to.
   * @param payload - The payload to send with the POST request. Must be a JSON string.
   * @returns A Promise that resolves to the response data.
   * @throws If an error occurs during the request. Errors are not re-thrown but handled by redirecting to a fallback page.
   */
  async post(url, payload) {
    return new Promise((resolve, reject) => {
      const client = this.httpClient;
      client.open('POST', url, true);
      client.setRequestHeader('Content-Type', 'application/json');
      client.onreadystatechange = () => {
        if (client.readyState === XMLHttpRequest.DONE || client.readyState === 4) {
          if (client.status === 200) {
            try {
              const response = JSON.parse(client.responseText);
              if (response && response.shouldRedirect && response.url) {
                redirectToUrl(response.url);
              }
              resolve(response);
            } catch (error) {
              this.redirectToFallbackPage();
              return;
            }
          } else if (client.status === 204) {
            resolve();
          } else if (client.status >= 400 && client.status <= 500) {
            this.redirectToFallbackPage();
            return;
          }
        }
      };
      client.onerror = () => {
        this.redirectToFallbackPage();
        return;
      };
      client.send(payload);
    });
  }

  /**
   * Performs a POST request to the specified URL with the given payload and returns a Promise that resolves to the response data.
   * If the response indicates a redirect, the client will be redirected to the specified URL.
   * If an error occurs during the request, the client will be redirected to a fallback page.
   *
   * @param url - The URL to send the POST request to.
   * @param payload - The payload to send with the POST request. Must be a FormData object.
   * @returns A Promise that resolves to the response data.
   * @throws If an error occurs during the request. Errors are not re-thrown but handled by redirecting to a fallback page.
   */
  postFormData(url, payload, additionalData) {
    const client = this.httpClient;
    const serializedPayload = this.serializePayload(payload);
    client.open('POST', url, true);
    client.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
    client.onreadystatechange = () => {
      if (client.readyState === XMLHttpRequest.DONE || client.readyState === 4) {
        if (client.status >= 200 && client.status < 400) {
          const confirmUrl = getConfirmationPageUrl(additionalData);
          redirectToUrl(confirmUrl);
          return;
        } else if (client.status >= 400 && client.status <= 500) {
          this.redirectToFallbackPage();
          return;
        }
      }
    };
    client.onerror = () => {
      this.redirectToFallbackPage();
      return;
    };
    client.send(serializedPayload);
  }
}